import * as Sentry from "@sentry/react";
import { QueryClient, QueryFunction } from "@tanstack/react-query";

export class ApiError extends Error {
  requestId?: string;
  constructor(message: string, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.requestId = requestId;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    // Prefer requestId from JSON body, fall back to the x-request-id response header
    // (the header is set by pino-http on every response, including explicit catch-block
    // responses that don't go through the global error middleware)
    const headerId = res.headers.get("x-request-id") ?? undefined;
    let message = `${res.status}: `;
    let requestId: string | undefined = headerId;
    try {
      const body = JSON.parse(text) as { error?: string; requestId?: string };
      message += body.error ?? text;
      if (body.requestId) requestId = body.requestId;
    } catch {
      message += text;
    }
    if (requestId) message += ` (ref: ${requestId})`;
    const err = new ApiError(message, requestId);
    // Capture server-side errors (5xx) to Sentry; skip client errors (4xx) as
    // they are expected flow (validation failures, unauthorised, not found).
    if (res.status >= 500) {
      Sentry.captureException(err, { extra: { requestId, url: res.url, status: res.status } });
    }
    throw err;
  }
}

function handleUnauthorized(res: Response) {
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("session-expired"));
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });
  } catch (networkErr) {
    // Network-level failure (DNS, offline, CORS preflight) — capture to Sentry
    Sentry.captureException(networkErr, { extra: { method, url } });
    throw networkErr;
  }

  handleUnauthorized(res);
  await throwIfResNotOk(res);
  return res;
}

export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const res = await fetch(url, { credentials: "include", ...options });
  handleUnauthorized(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    let res: Response;
    try {
      res = await fetch(queryKey.join("/") as string, {
        credentials: "include",
      });
    } catch (networkErr) {
      Sentry.captureException(networkErr, { extra: { queryKey } });
      throw networkErr;
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
